import { notify } from 'src/clients/ApiService';
import { ApiMethods, ApiTags, appAPI } from 'src/services/api/appApi';
import {
  CustomFieldEntity,
  CustomFieldEntityType,
  CustomFieldOperationType,
  CustomFieldOption,
} from 'src/store/clients/types';
import { ensureApiError } from 'src/utils/Errors';

/**
 * Get the appropriate snackbar message based on the custom field operation type.
 * Returns undefined for operations that should not show a snackbar.
 */
const getCustomFieldOperationMessage = (
  operationType?: CustomFieldOperationType,
): string | undefined => {
  switch (operationType) {
    case 'createOption':
      return 'Tag was created';
    case 'colorChange':
      return undefined; // No snackbar for color changes
    case 'updateOption':
    case 'deleteOption':
    case 'reorder':
    case 'createField':
    case 'updateField':
    case 'deleteField':
    default:
      return 'Custom Fields have been updated';
  }
};

export const customFieldsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getCustomFields: build.query<
      CustomFieldEntity,
      { type: CustomFieldEntityType }
    >({
      query: ({ type }) => ({
        path: `/entities/custom-fields?type=${type}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.customFields],
    }),
    updateCustomFields: build.mutation<
      CustomFieldEntity,
      {
        customFields: CustomFieldEntity;
        type: CustomFieldEntityType;
        operationType?: CustomFieldOperationType;
      }
    >({
      query: ({ customFields, type }) => ({
        path: `/entities/custom-fields?type=${type}`,
        method: ApiMethods.put,
        options: {
          body: customFields,
        },
      }),
      async onQueryStarted(
        { customFields, type, operationType },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: immediately update the cache with the new data
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'getCustomFields',
            { type },
            (draft) => {
              // Clear fields that are not in the new data
              Object.keys(draft).forEach((key) => {
                if (!(key in customFields)) {
                  delete draft[key as keyof CustomFieldEntity];
                }
              });

              // Apply the new custom fields
              Object.assign(draft, customFields);
            },
          ),
        );

        try {
          const result = await queryFulfilled;
          const updatedCustomFields = result.data;

          // Update the cache with the server response to ensure consistency
          dispatch(
            customFieldsApi.util.updateQueryData(
              'getCustomFields',
              { type },
              (draft) => {
                Object.keys(draft).forEach((key) => {
                  if (!(key in updatedCustomFields)) {
                    delete draft[key as keyof CustomFieldEntity];
                  }
                });
                Object.assign(draft, updatedCustomFields);
              },
            ),
          );

          const successMessage = getCustomFieldOperationMessage(operationType);
          if (successMessage) {
            notify({
              status: 'success',
              successMessage,
              dispatch,
            });
          }
        } catch (err) {
          // Rollback the optimistic update on error
          patchResult.undo();

          const { message: errorMessage } = ensureApiError(err);
          console.error('Error updating custom fields:', errorMessage);
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Custom Fields could not be updated',
            error: err,
            dispatch,
          });
        }
      },
    }),
  }),
});

export const { useGetCustomFieldsQuery, useUpdateCustomFieldsMutation } =
  customFieldsApi;

// Empty object constant to prevent re-rendering when additionalFields is undefined
const emptyPortalCustomFields: Record<string, CustomFieldOption> = {};

// Custom hook that handles undefined additionalFields to prevent re-rendering
export function useCustomFields(type: CustomFieldEntityType) {
  const { data: customFields } = useGetCustomFieldsQuery({
    type: type,
  });

  const portalCustomFields: Record<string, CustomFieldOption> =
    customFields?.additionalFields || emptyPortalCustomFields;

  return portalCustomFields;
}
